import { numFloatsPerNodeInstance } from '../../network/chart/Utils';
import {
  ColorQuadruplet,
  IUpdatePatternItem,
  PersistentFloat32Array,
  UpdateType,
} from '../../sharedComponents/webglUtils';
import {
  failIfValidOrNonExhaustive,
} from '../../Utils';
import {
  circleNumSegments,
  NodeInternal,
} from './nodeFillShadersUtils';

// Returns all multiples of (2 PI / numSegment)
const getNodeStrokeThetaValues = (numSegment: number) => {
  const result: number[] = [];
  const base = 2 * Math.PI / numSegment;
  for (let i = 0; i < numSegment; i += 1) {
    const theta = base * i;
    result.push(theta);
  }
  return result;
};

const getNodeStrokeIsPointOnCircumferenceValues = (numSegments: number) => {
  // Returns all 1's because every point is on the circumference:
  const result: number[] = [];
  for (let i = 0; i < numSegments; i += 1) {
    result.push(1);
  }
  return result;
};

// Returns the indices for the `numSegments` line segments that make up the circle's border
// e.g. 0, 1 (first line segment), 1, 2, (second line segment) 2, 3 (third line segment) etc
const getNodeStrokeIndices = (numSegments: number) => {
  const result: number[] = [];
  for (let j = 0; j < numSegments; j += 1) {
    result.push(j % numSegments);
    result.push((j + 1) % numSegments);
  }
  return result;
};

export const nodeStrokeThetaValues = getNodeStrokeThetaValues(circleNumSegments);
export const nodeStrokeIsPointOnCircumferenceValues = getNodeStrokeIsPointOnCircumferenceValues(circleNumSegments);
export const nodeStrokeIndices = getNodeStrokeIndices(circleNumSegments);

export const writeToBuffers = (input: {
    prevNodes: Map<string, NodeInternal>,
    nextNodes: Map<string, NodeInternal>,
    updatePattern: IUpdatePatternItem[],
    data: PersistentFloat32Array,
    isFirstRender: boolean,
  }) => {
  const {
    prevNodes, nextNodes, updatePattern, data, isFirstRender,
  } = input;
  const updatePatternLength = updatePattern.length;
  const buffer = data.buffer;
  data.length = updatePatternLength * numFloatsPerNodeInstance;

  for (let i = 0; i < updatePatternLength; i += 1) {
    const {key, type} = updatePattern[i];
    let initialCenterX: number, finalCenterX: number;
    let initialCenterY: number, finalCenterY: number;
    let initialRadius: number, finalRadius: number;
    let initialHalfStrokeWidth: number, finalHalfStrokeWidth: number;
    let initialColor: ColorQuadruplet, finalColor: ColorQuadruplet;

    if (type === UpdateType.Enter) {
      const node = nextNodes.get(key)!;
      initialCenterX = finalCenterX = node.centerX;
      initialCenterY = finalCenterY = node.centerY;
      // If this is the first render, set the initial radius to zero in order to
      // make the nodes "grow" into their full sizes:
      if (isFirstRender === true) {
        initialRadius = 1;
        finalRadius = node.radius;
      } else {
        initialRadius = finalRadius = node.radius;
      }
      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;
      initialColor = node.strokeColor.slice(0) as ColorQuadruplet;
      initialColor[3] = 0;
      finalColor = node.strokeColor.slice(0) as ColorQuadruplet;
    } else if (type === UpdateType.Exit) {
      const node = prevNodes.get(key)!;
      initialCenterX = finalCenterX = node.centerX;
      initialCenterY = finalCenterY = node.centerY;
      initialRadius = finalRadius = node.radius;
      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;

      initialColor = node.strokeColor.slice(0) as ColorQuadruplet;
      finalColor = node.strokeColor.slice(0) as ColorQuadruplet;
      finalColor[3] = 0;
    } else if (type === UpdateType.Update) {
      const prevNode = prevNodes.get(key)!;
      const nextNode = nextNodes.get(key)!;
      ({
        centerX: initialCenterX, centerY: initialCenterY, radius: initialRadius,
        /* tslint:disable-next-line:trailing-comma */
        strokeColor: initialColor,
      } = prevNode);
      ({
        centerX: finalCenterX, centerY: finalCenterY, radius: finalRadius,
        /* tslint:disable-next-line:trailing-comma */
        strokeColor: finalColor,
      } = nextNode);
      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;
    } else {
      failIfValidOrNonExhaustive(type, 'Invalid update type');
      initialCenterX = finalCenterX = 0;
      initialCenterY = finalCenterY = 0;
      initialRadius = finalRadius = 0;
      initialHalfStrokeWidth = finalHalfStrokeWidth = 0;
      initialColor = [0, 0, 0, 0];
      finalColor = [0, 0, 0, 0];
    }
    const start = i * numFloatsPerNodeInstance;
    buffer[start + 0] = initialCenterX;
    buffer[start + 1] = initialCenterY;

    buffer[start + 2] = finalCenterX;
    buffer[start + 3] = finalCenterY;

    buffer[start + 4] = initialRadius;
    buffer[start + 5] = finalRadius;

    buffer[start + 6] = initialHalfStrokeWidth;
    buffer[start + 7] = finalHalfStrokeWidth;

    buffer[start + 8] = initialColor[0];
    buffer[start + 9] = initialColor[1];
    buffer[start + 10] = initialColor[2];
    buffer[start + 11] = initialColor[0];

    buffer[start + 12] = finalColor[0];
    buffer[start + 13] = finalColor[1];
    buffer[start + 14] = finalColor[2];
    buffer[start + 15] = finalColor[3];

    buffer[start + 16] = 0;
  }

  return {
    instanceCount: updatePatternLength,
  };
};
